// TransportProPage — Consoles opérationnelles Transport Pro : là où chaque
// métier REÇOIT ses demandes (taxi → courses, car → réservations de billets,
// avion → réservations de vols, chauffeur privé → missions).
// DONNÉES SIMULÉES : les demandes arrivent automatiquement (démo) — branchement réel à venir.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, CarTaxiFront, BusFront, Plane, CarFront, MapPin, Clock,
  Check, X, BellRing, Wallet, ArrowUpRight, Users,
} from "lucide-react";
import "./transportpro.css";

const fmt = (n) => `${Number(n).toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, " ")} FCFA`;

// Pools de demandes entrantes simulées par métier.
const TABS = [
  {
    id: "taxi", label: "Taxi", Icon: CarTaxiFront, accent: "#F5C445", unit: "course",
    cta: { label: "Console chauffeur complète (carte, navigation)", to: "/chauffeur" },
    initial: [
      { id: "t1", title: "Cocody Riviera 3 → Plateau", sub: "Awa K. · à 450 m · paiement Wallet", price: 4000, meta: "≈ 15 min" },
      { id: "t2", title: "Angré 8e tranche → Zone 4", sub: "Jean-Marc D. · à 1,1 km · espèces", price: 6500, meta: "≈ 25 min" },
    ],
    pool: [
      { id: "t3", title: "Yopougon Niangon → Adjamé", sub: "Mariam S. · à 800 m · Wallet", price: 3500, meta: "≈ 18 min" },
      { id: "t4", title: "Plateau → Aéroport FHB", sub: "M. N'Guessan · à 300 m · Wallet", price: 8000, meta: "≈ 30 min" },
      { id: "t5", title: "Marcory → Treichville", sub: "Fatou B. · à 650 m · espèces", price: 2500, meta: "≈ 10 min" },
    ],
  },
  {
    id: "car", label: "Car interurbain", Icon: BusFront, accent: "#2EE27B", unit: "réservation",
    initial: [
      { id: "c1", title: "Abidjan → Bouaké · 2 places", sub: "Konan P. · départ 13:30 · Wallet", price: 14000, meta: "Car 70 pl. · 38/70" },
      { id: "c2", title: "Abidjan → Yamoussoukro · 1 place", sub: "Aïcha T. · départ 15:00 · espèces", price: 6500, meta: "Car 70 pl. · 52/70" },
    ],
    pool: [
      { id: "c3", title: "Abidjan → San-Pédro · 3 places", sub: "Famille Kouamé · départ 16:30 · Wallet", price: 27000, meta: "Car 70 pl. · 44/70" },
      { id: "c4", title: "Abidjan → Korhogo · 1 place", sub: "Ibrahim C. · départ 06:00 demain · Wallet", price: 12000, meta: "Car 70 pl. · 12/70" },
      { id: "c5", title: "Abidjan → Man · 2 places", sub: "Esther G. · départ 07:30 demain · espèces", price: 20000, meta: "Car 70 pl. · 8/70" },
    ],
  },
  {
    id: "avion", label: "Avion", Icon: Plane, accent: "#60A5FA", unit: "réservation",
    initial: [
      { id: "a1", title: "HF514 ABJ → San-Pédro · Éco", sub: "Mme Bamba · 12:40 aujourd'hui · Wallet", price: 85000, meta: "Siège 12A · 91 %" },
      { id: "a2", title: "HF318 ABJ → Bouaké · Éco", sub: "M. Koffi · 15:20 aujourd'hui · CB", price: 72000, meta: "Siège 4C · 76 %" },
    ],
    pool: [
      { id: "a3", title: "HF221 ABJ → Man · Business", sub: "Délégation CGECI (2 pax) · 17:05 · Wallet", price: 260000, meta: "Sièges 1A-1B · 64 %" },
      { id: "a4", title: "HF702 ABJ → Korhogo · Éco", sub: "Dr Ouattara · demain 10:15 · Wallet", price: 95000, meta: "Siège 9F · 87 %" },
      { id: "a5", title: "HF119 ABJ → Odienné · Éco", sub: "Mlle Cissé · demain 18:30 · CB", price: 88000, meta: "Siège 7B · 58 %" },
    ],
  },
  {
    id: "vtc", label: "Chauffeur privé", Icon: CarFront, accent: "#C084FC", unit: "mission",
    initial: [
      { id: "v1", title: "Aéroport FHB → Hôtel Ivoire", sub: "M. Diabaté · aujourd'hui 18:45 · berline", price: 25000, meta: "Accueil pancarte" },
      { id: "v2", title: "Mise à disposition journée", sub: "Mme Koné · demain 09:00 – 18:00 · SUV", price: 80000, meta: "Abidjan + Bingerville" },
    ],
    pool: [
      { id: "v3", title: "Cocody → Grand-Bassam A/R", sub: "M. Yao · samedi 10:00 · berline", price: 45000, meta: "Attente 3 h incluse" },
      { id: "v4", title: "Soirée VIP · Plateau", sub: "Délégation CGECI · vendredi 19:30 · SUV noir", price: 60000, meta: "Tenue formelle" },
      { id: "v5", title: "Navette mariage (cortège)", sub: "Famille Assi · samedi 08:00 · 2 véhicules", price: 150000, meta: "Décoration fournie" },
    ],
  },
];

export default function TransportProPage() {
  const navigate = useNavigate();
  const [tabId, setTabId] = useState("taxi");
  const [online, setOnline] = useState(true);
  // État par métier : { pending, accepted, poolIdx }
  const [state, setState] = useState(() => Object.fromEntries(
    TABS.map((t) => [t.id, { pending: [...t.initial], accepted: [], poolIdx: 0 }]),
  ));
  const onlineRef = useRef(online);
  onlineRef.current = online;

  const tab = TABS.find((t) => t.id === tabId);
  const st = state[tabId];
  const revenue = st.accepted.reduce((s, r) => s + r.price, 0);

  useEffect(() => { document.title = "AYA — Transport Pro"; window.scrollTo(0, 0); }, []);

  // Arrivée automatique de nouvelles demandes (SIMULATION) toutes les ~20 s.
  useEffect(() => {
    const timer = setInterval(() => {
      if (!onlineRef.current) return;
      setState((prev) => {
        const next = { ...prev };
        for (const t of TABS) {
          const s = prev[t.id];
          if (s.poolIdx < t.pool.length) {
            next[t.id] = { ...s, pending: [t.pool[s.poolIdx], ...s.pending], poolIdx: s.poolIdx + 1 };
            toast.info(`🔔 Nouvelle ${t.unit} — ${t.label}`);
            break; // une seule arrivée à la fois
          }
        }
        return next;
      });
    }, 20000);
    return () => clearInterval(timer);
  }, []);

  const accept = (req) => {
    setState((prev) => ({
      ...prev,
      [tabId]: { ...prev[tabId], pending: prev[tabId].pending.filter((r) => r.id !== req.id), accepted: [req, ...prev[tabId].accepted] },
    }));
    toast.success(`✅ ${tab.unit.charAt(0).toUpperCase() + tab.unit.slice(1)} acceptée · ${fmt(req.price)}`);
  };
  const refuse = (req) => {
    setState((prev) => ({
      ...prev,
      [tabId]: { ...prev[tabId], pending: prev[tabId].pending.filter((r) => r.id !== req.id) },
    }));
    toast(`Demande refusée — réaffectée à un autre opérateur.`);
  };

  return (
    <div className="tpro-page" data-testid="transport-pro-page">
      <header className="tpro-head">
        <button className="tpro-back" data-testid="tpro-back-btn" aria-label="Retour" onClick={() => navigate(-1)}><ChevronLeft size={18} /></button>
        <div>
          <h1>Transport <span>Pro</span></h1>
          <p>Réception des demandes — Taxi · Car · Avion · Chauffeur privé</p>
        </div>
        <span className="tpro-simu" data-testid="tpro-simulated-badge">DONNÉES SIMULÉES</span>
      </header>

      <div className="tpro-tabs" data-testid="tpro-tabs">
        {TABS.map((t) => {
          const nb = state[t.id].pending.length;
          return (
            <button key={t.id} className={`tpro-tab${tabId === t.id ? " on" : ""}`} data-testid={`tpro-tab-${t.id}`}
              style={tabId === t.id ? { borderColor: t.accent, color: t.accent } : undefined}
              onClick={() => setTabId(t.id)}>
              <t.Icon size={15} /> {t.label}
              {nb > 0 && <em className="tpro-pill" style={{ background: t.accent }}>{nb}</em>}
            </button>
          );
        })}
      </div>

      <div className="tpro-body" data-testid={`tpro-panel-${tab.id}`}>
        {/* Statut + compteurs du jour */}
        <div className="tpro-statusbar">
          <button className={`tpro-online${online ? " on" : ""}`} data-testid="tpro-online-toggle" onClick={() => { setOnline((v) => !v); toast.info(online ? "Vous êtes hors ligne — plus de nouvelles demandes." : "En ligne — vous recevez les demandes."); }}>
            <span className="dot" /> {online ? "En ligne" : "Hors ligne"}
          </button>
          <div className="tpro-counts">
            <span data-testid="tpro-count-pending"><BellRing size={13} color={tab.accent} /> {st.pending.length} en attente</span>
            <span data-testid="tpro-count-accepted"><Check size={13} color="#2EE27B" /> {st.accepted.length} acceptée{st.accepted.length > 1 ? "s" : ""}</span>
            <span data-testid="tpro-revenue"><Wallet size={13} color="#F5C445" /> {fmt(revenue)}</span>
          </div>
        </div>

        {/* FILE DE RÉCEPTION */}
        <section className="tpro-card">
          <h2><BellRing size={14} color={tab.accent} /> Demandes entrantes</h2>
          <div className="tpro-queue" data-testid="tpro-queue">
            {st.pending.length === 0 && <p className="tpro-empty" data-testid="tpro-queue-empty">Aucune demande en attente — les nouvelles {tab.unit}s apparaîtront ici en temps réel.</p>}
            {st.pending.map((r) => (
              <article key={r.id} className="tpro-req" data-testid={`tpro-req-${r.id}`} style={{ borderColor: `${tab.accent}55` }}>
                <div className="inf">
                  <strong><MapPin size={12} color={tab.accent} /> {r.title}</strong>
                  <span>{r.sub}</span>
                  <em><Clock size={10} /> {r.meta}</em>
                </div>
                <div className="act">
                  <strong style={{ color: tab.accent }}>{fmt(r.price)}</strong>
                  <div className="btns">
                    <button className="ok" data-testid={`tpro-accept-${r.id}`} onClick={() => accept(r)}><Check size={14} /> Accepter</button>
                    <button className="no" data-testid={`tpro-refuse-${r.id}`} onClick={() => refuse(r)} aria-label="Refuser"><X size={14} /></button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ACCEPTÉES */}
        <section className="tpro-card">
          <h2><Check size={14} color="#2EE27B" /> Acceptées — à honorer</h2>
          <div className="tpro-list" data-testid="tpro-accepted-list">
            {st.accepted.length === 0 && <p className="tpro-empty">Les {tab.unit}s acceptées s'affichent ici.</p>}
            {st.accepted.map((r) => (
              <div key={r.id} className="row">
                <div className="inf"><strong>{r.title}</strong><span>{r.sub}</span></div>
                <div className="val"><strong style={{ color: "#2EE27B" }}>{fmt(r.price)}</strong><em style={{ color: "#2EE27B" }}>confirmée</em></div>
              </div>
            ))}
          </div>
        </section>

        {tab.cta && (
          <button className="tpro-cta" data-testid="tpro-open-chauffeur" onClick={() => navigate(tab.cta.to)}>
            <Users size={14} /> {tab.cta.label} <ArrowUpRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
